import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarClock,
  Download,
  FileText,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
  TriangleAlert,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter, CardIcon, type CardSurface } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { ListCard } from '../ListCard'
import { ListCardHeader } from '../ListCardHeader'
import { ReportStatusPill } from './ReportStatusPill'
import { RunReportDialog } from './RunReportDialog'
import { ScheduleReportDialog } from './ScheduleReportDialog'
import { ReportsSheet } from './ReportsSheet'
import { AuthService } from '@/services/core/auth'
import { ReportsApi, type ReportSchedule, type ReportTarget } from '@/services/reports/reportsApi'
import { cadenceText, nextRunText } from '@/services/reports/reportCadence'
import { formatRelativeTime } from '../../utils/relativeTime'

/** Reports listed on the card itself; the rest are behind "view all". */
const PREVIEW_COUNT = 4

/** How often an unfinished report is checked on, in milliseconds. */
const POLL_MS = 5000

export interface ReportsCardProps {
  target: ReportTarget
  /** `sunken` for a read-only card beside a form; see {@link CardSurface}. */
  surface?: CardSurface
}

/**
 * Reports for one object: what is scheduled to arrive, and what has been
 * produced so far.
 *
 * Schedules come first — they are the thing that keeps working after the tab is
 * closed, and the card says in a line when each one next runs rather than
 * leaving a recurrence enum to be decoded.
 */
export function ReportsCard({ target, surface }: ReportsCardProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [runOpen, setRunOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editing, setEditing] = useState<ReportSchedule | undefined>(undefined)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ReportSchedule | null>(null)
  const enabled = AuthService.isAuthenticated() && !!target.id

  const executionsKey = ['report-executions', target.type, target.id]
  const schedulesKey = ['report-schedules', target.type, target.id]

  const { data: executions = [], isLoading } = useQuery({
    queryKey: executionsKey,
    queryFn: () => ReportsApi.getExecutions(target, 25),
    enabled,
    // A report is built in the background, so an unfinished one is checked on
    // until it lands.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((e) => e.status === 'PROCESSING' || e.status === 'PENDING')
        ? POLL_MS
        : false,
  })

  const { data: schedules = [] } = useQuery({
    queryKey: schedulesKey,
    queryFn: () => ReportsApi.getSchedules(target),
    enabled,
  })

  const setState = useMutation({
    mutationFn: ({ id, objectState }: { id: string; objectState: 'ACTIVE' | 'INACTIVE' }) =>
      ReportsApi.saveSchedule(id, { objectState }),
    onSuccess: (_data, variables) => {
      toast.success(variables.objectState === 'ACTIVE' ? t('reports.resumed') : t('reports.paused'))
      queryClient.invalidateQueries({ queryKey: schedulesKey })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => ReportsApi.remove(id),
    onSuccess: () => {
      toast.success(t('reports.scheduleDeleted'))
      queryClient.invalidateQueries({ queryKey: schedulesKey })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const openSchedule = (schedule?: ReportSchedule) => {
    setEditing(schedule)
    setScheduleOpen(true)
  }


  // An empty section says nothing the buttons above don't, so it is left out
  const showSchedules = schedules.length > 0
  const showRecent = isLoading || executions.length > 0

  return (
    <ListCard tone="insight" surface={surface}>
      <ListCardHeader
        title={
          <>
            <CardIcon icon={FileText} />
            {t('reports.title')}
          </>
        }
        count={executions.length}
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => setRunOpen(true)}>
              <Play className="size-4" />
              {t('reports.run')}
            </Button>
            <Button type="button" size="sm" onClick={() => openSchedule(undefined)}>
              <Plus className="size-4" />
              {t('reports.schedule')}
            </Button>
          </>
        }
      />

      {(showSchedules || showRecent) && (
        <CardContent className="flex flex-col gap-3">
          {/* ── Standing orders ─────────────────────────────────────────── */}
          {showSchedules && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('reports.schedules')}
              </span>
              {schedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="flex items-center gap-2 rounded-(--ad-radius) border p-2"
                >
                  <CalendarClock
                    className={cn(
                      'size-4 shrink-0',
                      schedule.objectState === 'ACTIVE'
                        ? 'text-(--ad-color-1)'
                        : 'text-(--ad-text-faint)'
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">{schedule.name}</div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="truncate">{cadenceText(schedule, t)}</span>
                      <span>·</span>
                      <span className="truncate">{nextRunText(schedule, t)}</span>
                      {schedule.warnings.length > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <TriangleAlert className="size-3.5 shrink-0 text-(--ad-badge-amber)" />
                          </TooltipTrigger>
                          <TooltipContent>{schedule.warnings.join('\n')}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" className="size-7">
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openSchedule(schedule)}>
                        <Pencil className="size-4" />
                        {t('common.edit')}
                      </DropdownMenuItem>
                      {schedule.reportUrl && (
                        <DropdownMenuItem asChild>
                          <a href={schedule.reportUrl} target="_blank" rel="noreferrer">
                            <Download className="size-4" />
                            {t('reports.openLatest')}
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() =>
                          setState.mutate({
                            id: schedule.id,
                            objectState: schedule.objectState === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                          })
                        }
                      >
                        {schedule.objectState === 'ACTIVE' ? (
                          <>
                            <Pause className="size-4" />
                            {t('reports.pause')}
                          </>
                        ) : (
                          <>
                            <Play className="size-4" />
                            {t('reports.resume')}
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setPendingDelete(schedule)}
                      >
                        <Trash2 className="size-4" />
                        {t('common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}

          {showSchedules && showRecent && <Separator />}

          {/* ── What has actually been produced ─────────────────────────── */}
          {showRecent && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('reports.recent')}
              </span>
              {isLoading && <Skeleton className="h-12 w-full rounded-lg" />}
              {executions.slice(0, PREVIEW_COUNT).map((execution) => (
                <div key={execution.id} className="flex items-center gap-2 py-1">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">
                      {execution.name || execution.templateName}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <ReportStatusPill status={execution.status} />
                      <span className="truncate">
                        {formatRelativeTime(execution.createTime, i18n.language)}
                      </span>
                      {execution.scheduleId && <CalendarClock className="size-3 shrink-0" />}
                    </div>
                  </div>
                  {execution.result && (
                    <Button type="button" variant="ghost" size="icon" className="size-7" asChild>
                      <a
                        href={execution.result}
                        target="_blank"
                        rel="noreferrer"
                        title={t('reports.open')}
                      >
                        <Download className="size-4" />
                      </a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {executions.length > PREVIEW_COUNT && (
        <CardFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setSheetOpen(true)}>
            {t('reports.viewAll', { count: executions.length })}
          </Button>
        </CardFooter>
      )}

      <RunReportDialog target={target} open={runOpen} onOpenChange={setRunOpen} />
      <ScheduleReportDialog
        target={target}
        schedule={editing}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
      />
      <ReportsSheet
        target={target}
        executions={executions}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('reports.deleteScheduleTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('reports.deleteScheduleDescription', { name: pendingDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) remove.mutate(pendingDelete.id)
                setPendingDelete(null)
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListCard>
  )
}
